import { observer } from "mobx-react-lite";
import {
  CheckCircle2,
  Circle,
  Layers3,
  SquareArrowOutUpRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CategoryBadge, MetricCodeBadge } from "@/components/common";
import { cn } from "@/lib/utils";
import { useDashboardFiltersStore } from "../../hooks/useDashboardStore";
import { getFrameworkLabel } from "../../utils/reporting-years";
import {
  buildDataEntryUrl,
  collapseWholeTreeSelection,
  expandCarriedOrgIds,
} from "../../utils/data-entry-link";

const STATUS_STYLES = {
  completed: {
    wrapper: "border-border/70 bg-card",
    icon: "border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    Icon: CheckCircle2,
  },
  missing: {
    wrapper: "border-border/70 bg-card",
    icon: "border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300",
    Icon: Circle,
  },
};

const FrameworkBadge = ({ framework, frameworkLabelByCode }) => {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background px-2 py-1 text-[10px] font-medium text-muted-foreground shadow-sm">
      <Layers3 className="h-3.5 w-3.5 text-muted-foreground" />
      <span className="max-w-[170px] truncate">
        {getFrameworkLabel(framework, frameworkLabelByCode)}
      </span>
    </span>
  );
};

const MetricListItem = observer(
  ({ metric, status, variant = "data", moduleName, frameworkLabelByCode }) => {
    const style = STATUS_STYLES[status] || STATUS_STYLES.missing;
    const Icon = style.Icon;
    const navigate = useNavigate();
    const { filters, available } = useDashboardFiltersStore();
    const showRedirect = variant === "data";
    const metricStatus = status === "completed";

    return (
      <div
        className={cn(
          "rounded-lg border px-3 py-3 shadow-sm transition-colors",
          style.wrapper,
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <span
              className={cn(
                "mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border",
                style.icon,
              )}
            >
              <Icon className="h-4 w-4" />
            </span>

            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold leading-5 text-foreground">
                {metric.metricTitle}
              </p>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                <MetricCodeBadge
                  code={metric.metricCode}
                  category={metric.category}
                />
                {metric.framework ? (
                  <FrameworkBadge
                    framework={metric.framework}
                    frameworkLabelByCode={frameworkLabelByCode}
                  />
                ) : null}
                <CategoryBadge category={metric.category} />
              </div>
            </div>
          </div>
          {showRedirect ? (
            <button
              type="button"
              onClick={() =>
                navigate(
                  // The API counts a picked parent's whole subtree for the
                  // drawer numbers, so the carried scope is the subtree
                  // closure of the selection (collapsed to "all" when it
                  // covers the whole tree), not the raw picked ids.
                  buildDataEntryUrl({
                    years: filters.years,
                    orgIds: collapseWholeTreeSelection(
                      expandCarriedOrgIds(filters.orgIds, available.orgTree),
                      available.orgTree,
                    ),
                    metricId: metric.metricId,
                    status: metricStatus,
                    moduleName,
                  }),
                )
              }
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border/70 bg-background text-muted-foreground shadow-sm transition hover:border-primary/40 hover:text-primary"
              aria-label="Go to Data Entry Page"
              title="Go to Data Entry Page"
            >
              <SquareArrowOutUpRight className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>
    );
  },
);

export default MetricListItem;
